import * as React from "react";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { formatDate } from "@/lib/format";

type DocumentStatus = "draft" | "published" | "archived";

type DocumentPreviewTooltipProps = {
  title: string;
  summary?: string;
  contentPreview?: string;
  status?: DocumentStatus;
  tags?: string[];
  updatedAt?: number;
  children: React.ReactNode;
};

function statusBadgeClass(status: DocumentStatus): string {
  if (status === "published") return "border-emerald-300 text-emerald-700 bg-emerald-50";
  if (status === "archived") return "border-zinc-300 text-zinc-700 bg-zinc-50";
  return "border-amber-300 text-amber-700 bg-amber-50";
}

export function DocumentPreviewTooltip({
  title,
  summary,
  contentPreview,
  status,
  tags,
  updatedAt,
  children,
}: DocumentPreviewTooltipProps) {
  return (
    <TooltipProvider delayDuration={500}>
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent
          side="right"
          align="start"
          className="max-w-sm space-y-2 bg-white p-4 text-foreground shadow-lg dark:bg-zinc-900"
        >
          <div className="space-y-1.5">
            <h4 className="font-semibold leading-tight">{title}</h4>
            {summary && <p className="text-sm text-muted-foreground">{summary}</p>}
          </div>

          {contentPreview && (
            <div className="border-t pt-2">
              <p className="text-xs text-muted-foreground">
                {contentPreview}
                {contentPreview.length === 200 ? "..." : ""}
              </p>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-1.5 border-t pt-2">
            {status && (
              <Badge variant="outline" className={statusBadgeClass(status)}>
                {status}
              </Badge>
            )}
            {updatedAt && (
              <Badge variant="outline" className="text-xs text-muted-foreground">
                {formatDate(updatedAt, { month: "short", day: "numeric" })}
              </Badge>
            )}
          </div>

          {tags && tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {tags.slice(0, 5).map((tag) => (
                <Badge key={tag} variant="secondary" className="text-xs">
                  {tag}
                </Badge>
              ))}
              {tags.length > 5 && (
                <Badge variant="secondary" className="text-xs">
                  +{tags.length - 5}
                </Badge>
              )}
            </div>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
